import { type QueryClient, mutationOptions, queryOptions } from "@tanstack/react-query";
import {
  getOperatorsHttpOrigin,
  type OperatorsAdminAuditResponse,
  type OperatorsAdminEffectiveResponse,
  type OperatorsAdminToolsResponse,
  type OperatorsAdminUsersResponse,
} from "../operators";
import {
  isOperatorsForbiddenError,
  OperatorsForbiddenError,
  operatorsQueryKeys,
} from "./operatorsReactQuery";
import { readOperatorsAuthErrorMessage, throwOperatorsAuthExpired } from "../operatorsAuthSession";

export { isOperatorsForbiddenError, OperatorsForbiddenError };

export const operatorsAdminQueryKeys = {
  all: [...operatorsQueryKeys.all, "admin"] as const,
  users: (query = "") => [...operatorsAdminQueryKeys.all, "users", query] as const,
  effective: (userId: string) => [...operatorsAdminQueryKeys.all, "effective", userId] as const,
  audit: (cursor: string | null = null) =>
    [...operatorsAdminQueryKeys.all, "audit", cursor] as const,
  tools: () => [...operatorsAdminQueryKeys.all, "tools"] as const,
  slackPrivateChannels: () => [...operatorsAdminQueryKeys.all, "slack-private-channels"] as const,
};

function throwIfUnauthorized(response: Response): void {
  if (response.status === 401) {
    throwOperatorsAuthExpired();
  }
}

async function throwIfForbidden(response: Response, fallbackMessage: string): Promise<void> {
  if (response.status !== 403) {
    return;
  }

  const message = await readOperatorsAuthErrorMessage(response).catch(() => fallbackMessage);
  throw new OperatorsForbiddenError(message || fallbackMessage);
}

async function adminFetch<T>(input: {
  fallbackError: string;
  forbiddenMessage: string;
  init?: RequestInit;
  path: string;
}): Promise<T> {
  const response = await fetch(`${getOperatorsHttpOrigin()}${input.path}`, {
    credentials: "include",
    ...input.init,
  });
  throwIfUnauthorized(response);
  await throwIfForbidden(response, input.forbiddenMessage);
  if (!response.ok) {
    throw new Error(await readOperatorsAuthErrorMessage(response).catch(() => input.fallbackError));
  }
  return (await response.json()) as T;
}

export function operatorsAdminUsersQueryOptions(input?: { enabled?: boolean; query?: string }) {
  const query = input?.query?.trim() ?? "";
  const path = query
    ? `/api/v1/admin/users?query=${encodeURIComponent(query)}`
    : "/api/v1/admin/users";

  return queryOptions({
    queryKey: operatorsAdminQueryKeys.users(query),
    queryFn: async () =>
      adminFetch<OperatorsAdminUsersResponse>({
        fallbackError: "Could not load admin users.",
        forbiddenMessage: "Super admin access required to manage users.",
        path,
      }),
    enabled: input?.enabled ?? true,
    staleTime: 10_000,
  });
}

export function operatorsAdminEffectiveQueryOptions(input: {
  enabled?: boolean;
  userId: string | null;
}) {
  const userId = input.userId ?? "";
  return queryOptions({
    queryKey: operatorsAdminQueryKeys.effective(userId),
    queryFn: async () =>
      adminFetch<OperatorsAdminEffectiveResponse>({
        fallbackError: "Could not load effective permissions.",
        forbiddenMessage: "Super admin access required.",
        path: `/api/v1/admin/users/${userId}/effective`,
      }),
    enabled: (input.enabled ?? true) && userId.length > 0,
    staleTime: 5_000,
  });
}

export function operatorsAdminAuditQueryOptions(input?: {
  cursor?: string | null;
  enabled?: boolean;
}) {
  const cursor = input?.cursor ?? null;
  const path = cursor
    ? `/api/v1/admin/audit?cursor=${encodeURIComponent(cursor)}`
    : "/api/v1/admin/audit";

  return queryOptions({
    queryKey: operatorsAdminQueryKeys.audit(cursor),
    queryFn: async () =>
      adminFetch<OperatorsAdminAuditResponse>({
        fallbackError: "Could not load governance audit events.",
        forbiddenMessage: "Super admin access required.",
        path,
      }),
    enabled: input?.enabled ?? true,
    staleTime: 10_000,
  });
}

export function operatorsAdminToolsQueryOptions(input?: { enabled?: boolean }) {
  return queryOptions({
    queryKey: operatorsAdminQueryKeys.tools(),
    queryFn: async () =>
      adminFetch<OperatorsAdminToolsResponse>({
        fallbackError: "Could not load tools and flags.",
        forbiddenMessage: "Super admin access required.",
        path: "/api/v1/admin/tools",
      }),
    enabled: input?.enabled ?? true,
    staleTime: 10_000,
  });
}

export function operatorsAdminSlackPrivateChannelsQueryOptions(input?: { enabled?: boolean }) {
  return queryOptions({
    queryKey: operatorsAdminQueryKeys.slackPrivateChannels(),
    queryFn: async () =>
      adminFetch<{ channels: string[] }>({
        fallbackError: "Could not load the Slack private channel allowlist.",
        forbiddenMessage: "Super admin access required.",
        path: "/api/v1/admin/slack/private-channels",
      }),
    enabled: input?.enabled ?? true,
    staleTime: 10_000,
  });
}

export function updateAdminSlackPrivateChannelsMutationOptions(input: {
  queryClient: QueryClient;
}) {
  return mutationOptions({
    mutationFn: async (variables: { channels: string[] }) =>
      adminFetch<{ channels: string[] }>({
        fallbackError: "Could not update the Slack private channel allowlist.",
        forbiddenMessage: "Super admin access required.",
        init: {
          body: JSON.stringify({ channels: variables.channels }),
          headers: { "content-type": "application/json" },
          method: "PUT",
        },
        path: "/api/v1/admin/slack/private-channels",
      }),
    onSuccess: async () => {
      await input.queryClient.invalidateQueries({
        queryKey: operatorsAdminQueryKeys.slackPrivateChannels(),
      });
    },
  });
}

export function grantAdminUserRoleMutationOptions(input: { queryClient: QueryClient }) {
  return mutationOptions({
    mutationFn: async (variables: { roleKey: string; userId: string }) =>
      adminFetch<{ roles: string[]; userId: string }>({
        fallbackError: "Could not grant role.",
        forbiddenMessage: "Super admin access required.",
        init: {
          body: JSON.stringify({ roleKey: variables.roleKey }),
          headers: { "content-type": "application/json" },
          method: "POST",
        },
        path: `/api/v1/admin/users/${variables.userId}/roles`,
      }),
    onSuccess: async () => {
      await input.queryClient.invalidateQueries({ queryKey: operatorsAdminQueryKeys.all });
    },
  });
}

export function revokeAdminUserRoleMutationOptions(input: { queryClient: QueryClient }) {
  return mutationOptions({
    mutationFn: async (variables: { roleKey: string; userId: string }) =>
      adminFetch<{ roles: string[]; userId: string }>({
        fallbackError: "Could not revoke role.",
        forbiddenMessage: "Super admin access required.",
        init: { method: "DELETE" },
        path: `/api/v1/admin/users/${variables.userId}/roles/${encodeURIComponent(variables.roleKey)}`,
      }),
    onSuccess: async () => {
      await input.queryClient.invalidateQueries({ queryKey: operatorsAdminQueryKeys.all });
    },
  });
}

export function updateAdminUserMutationOptions(input: { queryClient: QueryClient }) {
  return mutationOptions({
    mutationFn: async (variables: {
      isSuperAdmin?: boolean;
      status?: "active" | "suspended";
      userId: string;
    }) =>
      adminFetch<{ id: string; isSuperAdmin: boolean; status: "active" | "suspended" }>({
        fallbackError: "Could not update user.",
        forbiddenMessage: "Super admin access required.",
        init: {
          body: JSON.stringify({
            isSuperAdmin: variables.isSuperAdmin,
            status: variables.status,
          }),
          headers: { "content-type": "application/json" },
          method: "PATCH",
        },
        path: `/api/v1/admin/users/${variables.userId}`,
      }),
    onSuccess: async () => {
      await input.queryClient.invalidateQueries({ queryKey: operatorsAdminQueryKeys.all });
    },
  });
}

export function patchAdminFlagMutationOptions(input: { queryClient: QueryClient }) {
  return mutationOptions({
    mutationFn: async (variables: { enabled: boolean; key: string }) =>
      adminFetch<{ description: string; enabled: boolean; key: string }>({
        fallbackError: "Could not update feature flag.",
        forbiddenMessage: "Super admin access required.",
        init: {
          body: JSON.stringify({ enabled: variables.enabled }),
          headers: { "content-type": "application/json" },
          method: "PATCH",
        },
        path: `/api/v1/admin/flags/${encodeURIComponent(variables.key)}`,
      }),
    onSuccess: async () => {
      await input.queryClient.invalidateQueries({ queryKey: operatorsAdminQueryKeys.tools() });
      await input.queryClient.invalidateQueries({ queryKey: operatorsAdminQueryKeys.all });
    },
  });
}

export type SentryInvestigationRow = {
  id: string;
  threadId: string | null;
  issueId: string;
  status: string;
  trigger: string;
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  snapshot: { shortId: string; title: string; project: string };
  outcome: { kind: string; summary: string; url?: string; relatedRunId?: string } | null;
};
export type SentryInvestigationsResponse = {
  state: {
    paused: boolean;
    lastPollAt: string | null;
    lastError: string | null;
    activatedAt: string | null;
    backlogCompletedAt?: string | null;
  };
  configuration: {
    enabled: boolean;
    missingRequirements?: string[];
    maxConcurrent: number;
    maxDailyStarts: number;
    timeZone: string;
  };
  items: SentryInvestigationRow[];
  nextBefore: string | null;
};
export const sentryInvestigationsQueryKey = [
  ...operatorsAdminQueryKeys.all,
  "sentry-investigations",
] as const;
export function sentryInvestigationsQueryOptions(input?: { before?: string; enabled?: boolean }) {
  return queryOptions({
    queryKey: [...sentryInvestigationsQueryKey, input?.before ?? null],
    queryFn: () =>
      adminFetch<SentryInvestigationsResponse>({
        path: `/api/v1/admin/sentry-investigations${input?.before ? `?before=${encodeURIComponent(input.before)}` : ""}`,
        fallbackError: "Could not load Sentry investigations.",
        forbiddenMessage: "Super admin access required.",
      }),
    enabled: input?.enabled ?? true,
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}
export function changeSentryInvestigationsMutationOptions(input: { queryClient: QueryClient }) {
  return mutationOptions({
    mutationFn: (action: { paused: boolean } | { retryId: string }) =>
      adminFetch<{ paused?: boolean }>({
        path:
          "retryId" in action
            ? `/api/v1/admin/sentry-investigations/${action.retryId}/retry`
            : "/api/v1/admin/sentry-investigations",
        init:
          "retryId" in action
            ? { method: "POST" }
            : {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(action),
              },
        fallbackError: "Could not update Sentry investigations.",
        forbiddenMessage: "Super admin access required.",
      }),
    onSuccess: () =>
      input.queryClient.invalidateQueries({ queryKey: sentryInvestigationsQueryKey }),
  });
}
